import { Snackbar, AlertColor, Box, Stack, Typography, IconButton, Slide } from '@mui/material';
import React, { createContext, useContext, useState } from 'react';
import { TransitionProps } from '@mui/material/transitions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';

interface AlertProviderProps {
  children?: React.ReactNode;
}

export interface AlertStyles {
  fontSize?: string;
  textAlign?: 'left' | 'center' | 'right';
  width?: string;
}

const AlertContext = createContext((_message: string, _severity: AlertColor, _styles?: AlertStyles) => {});

export const useAlert = () => useContext(AlertContext);

function SlideTransition(props: TransitionProps & { children: React.ReactElement }) {
  return <Slide {...props} direction="left" />;
}

export function AlertProvider({ children }: AlertProviderProps) {
  const [message, setMessage] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [severity, setSeverity] = useState<AlertColor>('success');
  const [customStyles, setCustomStyles] = useState<AlertStyles>({});

  function showSnackbar(message: string, severity: AlertColor, styles?: AlertStyles) {
    setOpen(true);
    setMessage(message);
    setSeverity(severity);
    setCustomStyles(styles || {});
  }

  function onClose() {
    setOpen(false);
  }

  const getAlertIcon = () => {
    switch (severity) {
      case 'success':
        return <CheckCircleIcon sx={{ fontSize: 28, color: '#4caf50' }} />;
      case 'error':
        return <ErrorIcon sx={{ fontSize: 28, color: '#d32f2f' }} />;
      case 'warning':
        return <WarningIcon sx={{ fontSize: 28, color: '#ed6c02' }} />;
      case 'info':
        return <InfoIcon sx={{ fontSize: 28, color: '#0288d1' }} />;
      default:
        return <CheckCircleIcon sx={{ fontSize: 28, color: '#4caf50' }} />;
    }
  };

  const getAlertColor = () => {
    switch (severity) {
      case 'success':
        return { bg: '#f1f8f4', border: '#4caf50' };
      case 'error':
        return { bg: '#fef6f6', border: '#d32f2f' };
      case 'warning':
        return { bg: '#fff8f1', border: '#ed6c02' };
      case 'info':
        return { bg: '#f0f7fb', border: '#0288d1' };
      default:
        return { bg: '#f1f8f4', border: '#4caf50' };
    }
  };

  const colors = getAlertColor();

  return (
    <AlertContext.Provider value={showSnackbar}>
      {children}
      <>
        <Snackbar
          open={open}
          autoHideDuration={5000}
          onClose={onClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          TransitionComponent={SlideTransition}
          TransitionProps={{
            timeout: {
              enter: 400,
              exit: 300
            }
          }}
          sx={{
            mt: 6,
            mr: 2
          }}>
          <Box
            sx={{
              minWidth: '350px',
              maxWidth: '450px',
              backgroundColor: 'background.default',
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
              border: `2px solid ${colors.border}`,
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
            {/* Header */}
            <Box
              sx={{
                backgroundColor: colors.bg,
                px: 3,
                py: 2,
                borderBottom: `1px solid ${colors.border}20`
              }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                {getAlertIcon()}
                <Typography variant="h6" fontWeight={600} sx={{ flex: 1, color: 'text.primary' }}>
                  {severity === 'success'
                    ? 'Success'
                    : severity === 'error'
                      ? 'Error'
                      : severity === 'warning'
                        ? 'Warning'
                        : 'Information'}
                </Typography>
                <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>

            {/* Content */}
            <Box
              sx={{
                px: 3,
                py: 2.5
              }}>
              <Typography
                variant="body1"
                sx={{
                  fontSize: customStyles.fontSize || '0.95rem',
                  textAlign: customStyles.textAlign || 'left',
                  color: 'text.primary',
                  lineHeight: 1.6
                }}
                dangerouslySetInnerHTML={{ __html: message }}
              />
            </Box>
          </Box>
        </Snackbar>
      </>
    </AlertContext.Provider>
  );
}
